import { Outlet, useLocation } from "react-router-dom";
import { useAppShell } from "../../contexts/AppShellContext";
import { APP_NAV_CONFIGS } from "../../data/appNavConfig";
import HeaderRow1 from "./HeaderRow1";
import SideNav from "./SideNav";

// ── Props ───────────────────────────────────────────────────────

export interface LayoutShellProps {
  children?: React.ReactNode;
}

// ── LayoutShell ─────────────────────────────────────────────────

export default function LayoutShell({ children }: LayoutShellProps) {
  const { currentApp, entitledApps, switchApp } = useAppShell();
  const location = useLocation();

  // Look up nav config for the current sub-app
  const navConfig = currentApp ? APP_NAV_CONFIGS[currentApp.slug] ?? null : null;

  // Side nav items (flat list on the config)
  const sideNavItems = navConfig?.sideNavItems ?? [];

  // Fallback: unknown app slug → minimal layout (Row One only)
  if (!navConfig) {
    return (
      <div className="h-screen flex flex-col">
        <HeaderRow1
          currentApp={currentApp}
          entitledApps={entitledApps}
          onSwitchApp={switchApp}
        />
        <main className="flex-1 overflow-auto bg-slate-50/40">
          <div className="p-6">{children ?? <Outlet />}</div>
        </main>
      </div>
    );
  }

  return (
    <div className="h-screen flex flex-col">
      <HeaderRow1
        currentApp={currentApp}
        entitledApps={entitledApps}
        onSwitchApp={switchApp}
      />
      {navConfig.customSideNav ? (
        /* Sub-apps with customSideNav (e.g. Docs CMS): the sub-app owns
           everything below the header (sidebar, section tabs, content). */
        <div className="flex-1 overflow-hidden">
          {children ?? <Outlet />}
        </div>
      ) : (
        <div className="flex-1 flex overflow-hidden">
          {navConfig.sideNavWidth > 0 && sideNavItems.length > 0 && (
            <SideNav
              items={sideNavItems}
              activeItemPath={location.pathname}
              style={{ width: navConfig.sideNavWidth }}
            />
          )}
          <main className="flex-1 overflow-auto bg-slate-50/40">
            <div className="p-6">{children ?? <Outlet />}</div>
          </main>
        </div>
      )}
    </div>
  );
}
